import React, { useState } from 'react';
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  useTheme,
} from '@mui/material';

import Pagination from '@mui/material/Pagination';
import ORBApi from '../../../services/ORBApi';
import ExcelIcon from '../../../icons/ExcelIcon';
import MailIcon from '../../../icons/MailIcon';
import PDFLogo from '../../../assets/pdf-logo.png';
import Loading from '../../../components/common/Loading';
import { toast } from 'react-toastify';
import moment from 'moment';

export function ClientsTable(props) {
  const [loadingExcel, setLoadingExcel] = useState(0);
  const [loadingMail, setLoadingMail] = useState(0);
  const theme = useTheme();
  const order = props.order || 'desc';
  const orderBy = props.orderBy || 'reference_date';

  // Mapeamento entre headers e campos dos dados (suporta PT e EN)
  // IMPORTANTE: Os nomes aqui devem corresponder aos campos do backend
  // O backend aceita 'client' e mapeia internamente para 'client_name'
  const getFieldName = (headerTitle) => {
    const headerMap = {
      // Português
      'Data de Referência': 'reference_date',
      'Clientes': 'client', // Backend mapeia 'client' para 'client_name'
      'Montante': null, // Não ordenável
      'Download': null, // Não ordenável (botões)
      // Inglês
      'Reference Date': 'reference_date',
      'Clients': 'client', // Backend mapeia 'client' para 'client_name'
      'Amount': null, // Não ordenável
    };
    return headerMap[headerTitle] || null;
  };

  // Handler para mudança de ordenação - comunica ao componente pai
  const handleRequestSort = (property) => {
    if (!props.onSortChange) return;
    
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    const newOrderBy = property;
    
    props.onSortChange(newOrderBy, newOrder);
  };

  function sendReportToClientEmail(report) {
    setLoadingMail(1);
    let newReport = report;
    if (props.hasRole(['REPORTS_FULL_VIEW'])) {
      ORBApi.sendEmailDistribution(newReport)
        .then(() => toast.success(props.messages.report_send))
        .catch(() => toast.error(props.messages.report_send_error))
        .finally(() => {
          props.reports[report.index].state = false;
          setLoadingMail(0);
        });
    } else if (props.hasRole(['REPORTS_CLIENT_VIEW'])) {
      newReport.client_id = undefined;
      ORBApi.sendEmailDistribution(report)
        .then(() => toast.success(props.messages.report_send))
        .catch(() => toast.error(props.messages.report_send_error))
        .finally(() => {
          props.reports[report.index].state = false;
          setLoadingMail(0);
        });
    }
  }

  function downloadClientReport(report, download_type) {
    setLoadingExcel(1);
    let newReport = report;
    if (props.hasRole(['REPORTS_FULL_VIEW'])) {
      ORBApi.downloadClientDistribution(
        newReport.distribution_id,
        newReport.client_id,
      )
        .then((response) => {
          downloadFile(response, download_type);
        })
        .finally(() => {
          props.reports[report.index].state = false;
          setLoadingExcel(0);
        });
    } else if (props.hasRole(['REPORTS_CLIENT_VIEW'])) {
      newReport.client_id = undefined;

      ORBApi.downloadClientDistribution(
        newReport.distribution_id,
        newReport.client_id,
      )
        .then((response) => {
          downloadFile(response, download_type);
        })
        .finally(() => {
          props.reports[report.index].state = false;
          setLoadingExcel(0);
        });
    }
  }

  function downloadFile(response, download_type) {
    const file = new Blob([response.data], {
      type: `application/${download_type}`,
    });

    let fileName =
      response.headers['content-disposition'].split('filename=')[1];
    fileName = fileName.replaceAll('"', '');
    let link = document.createElement('a');
    link.href = window.URL.createObjectURL(file);
    link.download = fileName;
    link.click();
  }

  return (
    <>
      <TableContainer className="table-container" component={Paper}>
        <Table
          size="small"
          aria-label="collapsible table"
          className="basic-table"
        >
          <TableHead className="basic-table-head">
            <TableRow>
              {props.headers_table.map((title, key) => {
                const fieldName = getFieldName(title);
                const isSortable = fieldName !== null;
                
                return (
                  <TableCell
                    key={key}
                    align="center"
                    style={{ color: '#F5F5F5' }}
                    sortDirection={orderBy === fieldName ? order : false}
                  >
                    {isSortable ? (
                      <TableSortLabel
                        active={orderBy === fieldName}
                        direction={orderBy === fieldName ? order : 'asc'}
                        onClick={() => handleRequestSort(fieldName)}
                        sx={{
                          '&.Mui-active': {
                            color: theme.palette.secondary.main,
                          },
                          '& .MuiTableSortLabel-icon': {
                            color: orderBy === fieldName 
                              ? `${theme.palette.secondary.main} !important` 
                              : 'rgba(255, 255, 255, 0.54)',
                          },
                          '&:hover': {
                            color: theme.palette.secondary.light,
                          },
                          '&:hover .MuiTableSortLabel-icon': {
                            color: `${theme.palette.secondary.light} !important`,
                          },
                          color: orderBy === fieldName 
                            ? theme.palette.secondary.main 
                            : 'inherit',
                        }}
                      >
                        {title}
                      </TableSortLabel>
                    ) : (
                      title
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {props.reports?.length > 0
              ? props.reports.map((report, key) => {
                  return (
                    <TableRow key={key} className="row">
                      <TableCell
                        className={`clickable ${
                          report.active ? 'active-row' : ''
                        }`}
                        align="center"
                        onClick={() => {
                          report.index = key;
                          props.onRowClick(report);
                        }}
                      >
                        {moment(report.reference_date).format('MM/YYYY')}
                      </TableCell>
                      <TableCell
                        className={`clickable ${
                          report.active ? 'active-row' : ''
                        }`}
                        align="center"
                        onClick={() => {
                          report.index = key;
                          props.onRowClick(report);
                        }}
                      >
                        {report.client}
                      </TableCell>
                      <TableCell
                        className={`clickable ${
                          report.active ? 'active-row' : ''
                        }`}
                        align="center"
                        onClick={() => {
                          report.index = key;
                          props.onRowClick(report);
                        }}
                      >
                        {props.formatCurrency(report.total, report.currency)}
                      </TableCell>
                      <TableCell
                        className={`clickable ${
                          report.active ? 'active-row' : ''
                        }`}
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          gap: '5px',
                        }}
                      >
                        {loadingExcel && report.state ? (
                          <Loading size="15px" />
                        ) : (
                          <ExcelIcon
                            fontSize="small"
                            className="clickable action-icon"
                            onClick={() => {
                              report.state = true;
                              report.index = key;
                              downloadClientReport(report, 'excel');
                            }}
                          />
                        )}

                        {loadingMail && report.state ? (
                          <Loading size="15px" />
                        ) : (
                          <MailIcon
                            fontSize="small"
                            className="clickable action-icon"
                            onClick={() => {
                              report.state = true;
                              report.index = key;
                              sendReportToClientEmail(report);
                            }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              : null}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination
        shape="rounded"
        className="table-pagination"
        count={props.pages || 1}
        page={props.current_page || 1}
        onChange={(e, value) => {
          props.onPageChange(value);
        }}
      />
    </>
  );
}

export function ContributorsTable(props) {
  const [loadingMail, setLoadingMail] = useState(0);
  const [loadingExcel, setLoadingExcel] = useState(0);
  const [loadingPdf, setLoadingPdf] = useState(0);
  const [loadingZip, setLoadingZip] = useState(0);
  const theme = useTheme();
  const order = props.order || 'asc';
  const orderBy = props.orderBy || 'reference_date';

  // Baixa TODOS os relatorios da distribuicao num unico .zip.
  // Os parametros saem da propria listagem (1a linha) + filtros aplicados.
  const firstRow = props.contributors && props.contributors[0];

  function downloadAllReportsZip() {
    if (!firstRow) return;

    setLoadingZip(1);
    ORBApi.downloadContributorsReportsZip({
      distribution_id: firstRow.distribution_id,
      client_id: firstRow.client_id,
      contributor_name: props.contributor_name,
    })
      .then((response) => {
        downloadFile(response, 'zip');
      })
      .catch((error) => {
        const status = error?.response?.status;
        if (status === 400) {
          toast.error(
            props.messages?.too_many_reports ||
              'Muitos titulares para baixar de uma vez. Aplique um filtro.',
          );
        } else {
          toast.error(props.messages?.error || 'Erro ao baixar os relatórios');
        }
      })
      .finally(() => setLoadingZip(0));
  }

  // Mapeamento entre headers e campos dos dados (suporta PT e EN)
  // IMPORTANTE: Os nomes aqui devem corresponder aos campos do backend
  const getFieldName = (headerTitle) => {
    const headerMap = {
      // Português
      'Data de referência': 'reference_date',
      'Titular': 'contributor',
      'Montante': 'total',
      'Cliente': 'client',
      'Download': null, // Não ordenável (botões)
      // Inglês
      'Reference Date': 'reference_date',
      'Holder': 'contributor',
      'Amount': 'total',
      'Client': 'client',
    };
    return headerMap[headerTitle] || null;
  };

  // Handler para mudança de ordenação - comunica ao componente pai
  const handleRequestSort = (property) => {
    if (!props.onSortChange) return;
    
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    const newOrderBy = property;
    
    props.onSortChange(newOrderBy, newOrder);
  };



  function sendReportToContributorEmail(report) {
    setLoadingMail(1);
    let newReport = report;
    if (props.hasRole(['REPORTS_FULL_VIEW'])) {
      ORBApi.downloadReportPdf(newReport, 'pdf', 1)
        .then(() => toast.success(props.messages.report_send))
        .catch(() => toast.error(props.messages.report_send_error))
        .finally(() => {
          props.contributors[report.index].state = false;
          setLoadingMail(0);
        });
    } else {
      if (props.hasRole(['REPORTS_ARTIST_VIEW'])) {
        newReport.contributor_id = null;
      }
      ORBApi.downloadReportPdf(newReport, 'pdf', 1)
        .then(() => toast.success(props.messages.report_send))
        .catch(() => toast.error(props.messages.report_send_error))
        .finally(() => {
          props.contributors[report.index].state = false;
          setLoadingMail(0);
        });
    }
  }

  function downloadContributorReport(report, download_type) {
    if (download_type === 'pdf') {
      setLoadingPdf(1);
      let newReport = report;
      if (props.hasRole(['REPORTS_FULL_VIEW'])) {
        ORBApi.downloadReportPdf(newReport, download_type)
          .then((data) => {
            downloadFile(data, download_type);
          })
          .finally(() => {
            setLoadingPdf(0);

            props.contributors[report.index].state = false;
          });
      } else {
        if (props.hasRole(['REPORTS_ARTIST_VIEW'])) {
          newReport.contributor_id = null;
        }
        ORBApi.downloadReportPdf(newReport, download_type)
          .then((response) => {
            downloadFile(response, download_type);
          })
          .finally(() => {
            setLoadingPdf(0);

            props.contributors[report.index].state = false;
          });
      }
    } else {
      setLoadingExcel(1);
      let newReport = report;
      if (props.hasRole(['REPORTS_FULL_VIEW'])) {
        ORBApi.downloadReportExcel(newReport, download_type)
          .then((response) => {
            downloadFile(response, download_type);
          })
          .finally(() => {
            setLoadingExcel(0);

            props.contributors[report.index].state = false;
          });
      } else if (
        props.hasRole(['REPORTS_CLIENT_VIEW', 'REPORTS_ARTIST_VIEW', 'REPORTS_AGENT_VIEW'])
      ) {
        ORBApi.downloadReportExcel(newReport, download_type)
          .then((data) => {
            downloadFile(data, download_type);
          })
          .finally(() => {
            setLoadingExcel(0);

            props.contributors[report.index].state = false;
          });
      }
    }
  }

  function downloadFile(response, download_type) {
    const file = new Blob([response.data], {
      type: `application/${download_type}`,
    });

    let fileName =
      response.headers['content-disposition'].split('filename=')[1];
    fileName = fileName.replaceAll('"', '');
    let link = document.createElement('a');
    link.href = window.URL.createObjectURL(file);
    link.download = fileName;
    link.click();
  }

  return (
    <>
      {firstRow ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            // acompanha a largura/centralizacao da .table-container (max-width:95%; margin:0 auto)
            maxWidth: '95%',
            // o espaco abaixo vem do margin-top da propria .table-container
            margin: '20px auto 0 auto',
          }}
        >
          {loadingZip ? (
            <Loading size="25px" />
          ) : (
            <button
              type="button"
              className="download-all-btn"
              onClick={downloadAllReportsZip}
              title={
                props.messages?.download_all_title ||
                'Baixar todos os relatórios em um .zip'
              }
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                border: 'none',
                borderRadius: '4px',
                padding: '6px 12px',
                background: theme.palette.secondary.main,
                color: '#fff',
                fontSize: '0.85rem',
              }}
            >
              <ExcelIcon />
              {props.messages?.download_all || 'Baixar todos (.zip)'}
            </button>
          )}
        </div>
      ) : null}

      <TableContainer className="table-container" component={Paper}>
        <Table
          size="small"
          aria-label="collapsible table"
          className="basic-table"
        >
          <TableHead className="basic-table-head">
            <TableRow>
              {props.headers_table.map((title, key) => {
                const fieldName = getFieldName(title);
                const isSortable = fieldName !== null;
                
                return (
                  <TableCell
                    key={key}
                    align="center"
                    style={{ color: '#F5F5F5' }}
                    sortDirection={orderBy === fieldName ? order : false}
                  >
                    {isSortable ? (
                      <TableSortLabel
                        active={orderBy === fieldName}
                        direction={orderBy === fieldName ? order : 'asc'}
                        onClick={() => handleRequestSort(fieldName)}
                        sx={{
                          '&.Mui-active': {
                            color: theme.palette.secondary.main,
                          },
                          '& .MuiTableSortLabel-icon': {
                            color: orderBy === fieldName 
                              ? `${theme.palette.secondary.main} !important` 
                              : 'rgba(255, 255, 255, 0.54)',
                          },
                          '&:hover': {
                            color: theme.palette.secondary.light,
                          },
                          '&:hover .MuiTableSortLabel-icon': {
                            color: `${theme.palette.secondary.light} !important`,
                          },
                          color: orderBy === fieldName 
                            ? theme.palette.secondary.main 
                            : 'inherit',
                        }}
                      >
                        {title}
                      </TableSortLabel>
                    ) : (
                      title
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {props.contributors?.length > 0
              ? props.contributors.map((contributor, key) => {
                  return (
                    <TableRow key={contributor.id || contributor.contributor_id || key} className="row">
                      <TableCell
                        className="clickable"
                        align="center"
                        onClick={() => props.tableHandler(contributor)}
                      >
                        {moment(contributor.reference_date).format('MM/YYYY')}
                      </TableCell>
                      <TableCell
                        className="clickable"
                        align="center"
                        onClick={() => props.tableHandler(contributor)}
                      >
                        {contributor.contributor}
                      </TableCell>
                      <TableCell
                        className="clickable"
                        align="center"
                        onClick={() => props.tableHandler(contributor)}
                      >
                        {props.formatCurrency(
                          contributor.total,
                          contributor.currency,
                        )}
                      </TableCell>
                      <TableCell
                        className="clickable"
                        align="center"
                        onClick={() => props.tableHandler(contributor)}
                      >
                        {contributor.client}
                      </TableCell>
                      <TableCell
                        className="clickable"
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: '5px',
                        }}
                      >
                        {loadingExcel && contributor.state ? (
                          <Loading size="15px" />
                        ) : (
                          <ExcelIcon
                            fontSize="small"
                            className="clickable action-icon"
                            onClick={() => {
                              contributor.state = true;
                              contributor.index = key;
                              downloadContributorReport(
                                contributor,
                                'excel',
                                'xls',
                              );
                            }}
                          />
                        )}
                        {loadingPdf && contributor.state ? (
                          <Loading size="15px" />
                        ) : (
                          <img
                            src={PDFLogo}
                            height="25px"
                            alt="PDF logo"
                            className="clickable action-icon"
                            onClick={() => {
                              contributor.state = true;
                              contributor.index = key;
                              downloadContributorReport(
                                contributor,
                                'pdf',
                                'pdf',
                              );
                            }}
                          />
                        )}
                        {loadingMail && contributor.state ? (
                          <Loading size="15px" />
                        ) : (
                          <MailIcon
                            fontSize="small"
                            className="clickable action-icon"
                            onClick={() => {
                              contributor.state = true;
                              contributor.index = key;
                              sendReportToContributorEmail(contributor);
                            }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              : null}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination
        shape="rounded"
        className="table-pagination"
        count={props.pages || 1}
        page={props.current_page || 1}
        onChange={(e, value) => {
          props.onPageChange(value);
        }}
      />
    </>
  );
}
